import {
  Accordion,
  Container,
  Flex,
  Image,
  Text,
  Title,
  Box,
} from '@mantine/core';
import { ChevronUpIcon } from 'lucide-react';
import { useState } from 'react';

interface FeatureItem {
  value: string;
  title: string;
  description: string;
  media: {
    type: 'image' | 'video';
    src: string;
    alt: string;
    w: number;
    h: number;
  };
}

const FeatureAccordion = () => {
  const items: FeatureItem[] = [
    {
      value: 'feature-1',
      title: 'Intake and Fees Made Easy',
      description:
        'Automatically capture client information, generate engagement letters, and open cases—all in one system. Easily handle hourly, flat fees, subscriptions, and legal insurance without workarounds.',
      media: {
        type: 'video',
        src: '/client-onboarding.mp4',
        alt: 'Intake and Fees Made Easy',
        w: 600,
        h: 400,
      },
    },
    {
      value: 'feature-2',
      title: 'Documents Made Easy',
      description:
        'Save directly from Word and Google Docs, retain full control of your documents, and access everything from one place. No migration headaches, no third-party apps required.',
      media: {
        type: 'video',
        src: '/documents-made-easy.mp4',
        alt: 'Documents Made Easy',
        w: 600,
        h: 400,
      },
    },
    {
      value: 'feature-3',
      title: 'Spotlight Search and Actions',
      description:
        'Search anything or do anything in the app using ⌘K (Mac) or Ctrl+K (Windows). Quick access to cases, clients, documents, and actions—all from one command palette.',
      media: {
        type: 'video',
        src: '/spotlight-search.mp4',
        alt: 'Spotlight Search and Actions',
        w: 600,
        h: 400,
      },
    },
    {
      value: 'feature-4',
      title: 'AI Admin Assistant',
      description:
        'Scout, our AI assistant, can do anything a human can within the app—from drafting documents to doing your time entries. All while keeping your data confidential and never training on client information.',
      media: {
        type: 'video',
        src: '/ai-admin-assistant.mp4',
        alt: 'AI Admin Assistant',
        w: 600,
        h: 400,
      },
    },
  ];

  const [selectedValue, setSelectedValue] = useState<string>(items[0].value);
  const maxMediaHeight = items.reduce(
    (max, item) => Math.max(max, item.media.h),
    0,
  );

  return (
    <Container
      size='lg'
      pt='lg'
      pb='xl'
      style={{
        overflow: 'hidden',
        borderRadius: 'var(--mantine-radius-lg)',
      }}
    >
      <Flex
        justify='space-between'
        gap='calc(var(--mantine-spacing-lg) * 3)'
        p={{
          base: 0,
          sm: 'calc(var(--mantine-spacing-lg) * 2)',
        }}
        wrap={{
          base: 'wrap',
          lg: 'nowrap',
        }}
        maw='100%'
      >
        <Flex justify='center' align='center'>
          <Accordion
            chevron={<ChevronUpIcon size={24} />}
            chevronSize={24}
            value={selectedValue}
            onChange={(value) => {
              if (value === null) return;
              setSelectedValue(value);
            }}
            styles={{
              control: {
                height: 60,
              },
            }}
          >
            {items.map((item) => (
              <Accordion.Item key={item.value} value={item.value}>
                <Accordion.Control>
                  <Title c='var(--mantine-color-text)' order={4} fz='xl'>
                    {item.title}
                  </Title>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text
                    fz={{
                      base: 'sm',
                      sm: 'md',
                    }}
                  >
                    {item.description}
                  </Text>
                  {item.media.type === 'video' ? (
                    <Box
                      my='xl'
                      hiddenFrom='md'
                      style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: 'var(--mantine-radius-md)',
                        overflow: 'hidden',
                      }}
                    >
                      <video
                        src={item.media.src}
                        autoPlay
                        loop
                        muted
                        playsInline
                        key={item.media.src}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                        }}
                        aria-label={item.media.alt}
                      />
                    </Box>
                  ) : (
                    <Image
                      my='xl'
                      w='100%'
                      h='auto'
                      hiddenFrom='md'
                      alt={item.media.alt}
                      src={item.media.src}
                    />
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Flex>
        <Flex visibleFrom='md' maw='60%' align='center' h={maxMediaHeight}>
          {items.map((item) => (
            <Flex
              key={item.value}
              visibleFrom='md'
              justify='flex-start'
              align='center'
              ml='xl'
              style={{
                display: selectedValue === item.value ? 'initial' : 'none',
              }}
            >
              {item.media.type === 'video' ? (
                <Box
                  style={{
                    width: item.media.w,
                    height: item.media.h,
                    borderRadius: 'var(--mantine-radius-md)',
                    overflow: 'hidden',
                  }}
                >
                  <video
                    src={item.media.src}
                    autoPlay
                    loop
                    muted
                    playsInline
                    key={item.media.src}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                    aria-label={item.media.alt}
                  />
                </Box>
              ) : (
                <Image
                  src={item.media.src}
                  alt={item.media.alt}
                  w={item.media.w}
                  h={item.media.h}
                />
              )}
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Container>
  );
};

export default FeatureAccordion;
